import { AttractorContentType, PromotionSearchResult } from "../types";

/**
 * Pure state for the attractor content-classification fields, kept apart from the UI so the
 * gaming-flag rules can be tested without rendering anything.
 */

export type LinkableContentType = Exclude<AttractorContentType, "General">;

export interface AttractorContentValue {
  contentType: AttractorContentType;
  linkedPromotion: PromotionSearchResult | null;
  /**
   * The user's explicit answer to "Gaming content only". Null means they have not given one,
   * so the API detects it from the linked promotion when the attractor is saved.
   */
  gamingOverride: boolean | null;
}

export const EMPTY_CONTENT_VALUE: AttractorContentValue = {
  contentType: "General",
  linkedPromotion: null,
  gamingOverride: null,
};

export const isLinkable = (
  contentType: AttractorContentType
): contentType is LinkableContentType =>
  contentType === "Game" || contentType === "Promotion";

/**
 * Seeds the fields from a saved attractor.
 *
 * The saved gaming flag becomes an explicit override, even though it may have been detected
 * rather than chosen: the API does not record which it was, and re-detecting on every edit
 * would silently undo a manual choice. Changing the linked promotion clears the override, so
 * detection runs again whenever it could give a different answer.
 */
export function contentValueFrom(attractor: {
  contentType?: AttractorContentType | null;
  linkedPromotionId?: number | null;
  linkedPromotionName?: string | null;
  isGamingContent?: boolean;
}): AttractorContentValue {
  const contentType = attractor.contentType ?? "General";
  const linkedPromotion =
    isLinkable(contentType) && attractor.linkedPromotionId != null
      ? {
          promotionId: attractor.linkedPromotionId,
          displayName:
            attractor.linkedPromotionName ?? `#${attractor.linkedPromotionId}`,
        }
      : null;
  return {
    contentType,
    linkedPromotion,
    gamingOverride: attractor.isGamingContent ?? false,
  };
}

export type GamingCheckboxHint =
  "manual" | "game-auto" | "promotion-auto" | "general";

export interface GamingCheckboxState {
  checked: boolean | "indeterminate";
  hint: GamingCheckboxHint;
  /** Whether "use automatic detection" makes sense from here. */
  canReset: boolean;
}

/**
 * What the "Gaming content only" checkbox shows.
 *
 * - Game with no override: ticked, because games are always gaming content.
 * - Promotion with no override: indeterminate, because the answer comes from the API on save.
 * - General: whatever the user set; there is nothing to detect from.
 */
export function gamingCheckboxState(
  value: AttractorContentValue
): GamingCheckboxState {
  const { contentType, gamingOverride } = value;

  if (contentType === "General") {
    return {
      checked: gamingOverride ?? false,
      hint: "general",
      canReset: false,
    };
  }
  if (gamingOverride !== null) {
    return { checked: gamingOverride, hint: "manual", canReset: true };
  }
  if (contentType === "Game") {
    return { checked: true, hint: "game-auto", canReset: false };
  }
  return { checked: "indeterminate", hint: "promotion-auto", canReset: false };
}

/** Switching content type starts the link and the gaming flag over. */
export function withContentType(
  value: AttractorContentValue,
  contentType: AttractorContentType
): AttractorContentValue {
  if (contentType === value.contentType) return value;
  return { contentType, linkedPromotion: null, gamingOverride: null };
}

/** Choosing a different promotion hands the gaming flag back to detection. */
export function withLinkedPromotion(
  value: AttractorContentValue,
  linkedPromotion: PromotionSearchResult | null
): AttractorContentValue {
  if (linkedPromotion?.promotionId === value.linkedPromotion?.promotionId) {
    return { ...value, linkedPromotion };
  }
  return { ...value, linkedPromotion, gamingOverride: null };
}

export function toClassificationRequest(value: AttractorContentValue) {
  return {
    contentType: value.contentType,
    linkedPromotionId: isLinkable(value.contentType)
      ? (value.linkedPromotion?.promotionId ?? null)
      : null,
    // General has nothing to detect from, so an unset flag is an explicit "no".
    isGamingContentOverride:
      value.contentType === "General"
        ? (value.gamingOverride ?? false)
        : value.gamingOverride,
  };
}

export const linkNoun = (contentType: LinkableContentType) =>
  contentType === "Game" ? "game" : "promotion";

/**
 * Case-insensitive name match, capped so a venue with hundreds of promotions does not render
 * hundreds of rows.
 */
export function filterPromotions(
  promotions: PromotionSearchResult[],
  query: string,
  limit: number
): { matches: PromotionSearchResult[]; total: number } {
  const needle = query.trim().toLowerCase();
  const all = needle
    ? promotions.filter((p) => p.displayName.toLowerCase().includes(needle))
    : promotions;
  return { matches: all.slice(0, limit), total: all.length };
}
